<template>
  <li class="q-pt-1 multi-range__container">
    <div class="q-d-flex q-justify-content-between q-pb-3 multi-range__item">
      <small class="q-small multi-range__min-container">
        <span class="multi-range__min-text">{{ SmartSearchTranslations.filters.from }}: </span>
        <span class="multi-range__min-value">{{ format(filterData.min) }}</span>
      </small>
      <small class="q-small multi-range__max-container">
        <span class="multi-range__max-text q-ps-2">{{ SmartSearchTranslations.filters.to }}: </span>
        <span class="multi-range__max-value">{{ format(filterData.max) }}</span>
      </small>
    </div>
    <Slider :class="'q-px-3'" v-model="range" :format="format" :min="minInputValue" :max="maxInputValue"
            @end="updateRangeValue"
    />
  </li>
</template>

<script setup lang="ts">
import Slider from '@vueform/slider'
import {defineEmits, defineProps, inject, ref, watch} from 'vue';
import {useFiltersStore} from "@/store/SerpPage/FiltersStore";
import {useSerpResultsStore} from "@/store/SerpPage/SerpResultsStore";
import {storeToRefs} from "pinia";
import {SmartSearchConfig} from "@/main";
import {formatPrice} from "@/core/utils";
import {SmartSearchTranslationsInterface} from "@/core/types";

const filtersStore = useFiltersStore()

const props = defineProps(['filterData', 'field'])
const minInputValue = ref<number | null>(props.filterData.min);
const maxInputValue = ref<number | null>(props.filterData.max);
const unit = ref(props.filterData.unit)
const serpStore = useSerpResultsStore()
const {getCurrencyCode} = storeToRefs(serpStore)
const {removeDecimals} = SmartSearchConfig.value.appConfiguration
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const SmartSearchTranslations = inject<SmartSearchTranslationsInterface>('SmartSearchTranslations')!


const format = (value: number) => {
  if (props.field === 'price') {
    return formatPrice(value, getCurrencyCode.value, removeDecimals)
  }
  return `${value.toFixed()} ${unit.value ?? ''}`;
};
const range = ref([minInputValue.value, maxInputValue.value]);

const emit = defineEmits(['closeDropdown']);
const updateRangeValue = (newValues: Array<number>) => {
  const minValue = newValues[0]
  const maxValue = newValues[1]
  setTimeout(() => {
    filtersStore.updateRangeFilter(props.field, minValue, maxValue);
    emit('closeDropdown');
  }, 400)

}
watch(props.filterData, () => {
  filtersStore.updateRangeFilter(props.field, Number(minInputValue.value), Number(maxInputValue.value));
}, {immediate: false});


</script>

<style>

#smartsearch-serp .slider-target, #smartsearch-serp .slider-target * {
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  box-sizing: border-box;
  touch-action: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none
}

#smartsearch-serp .slider-target {
  position: relative
}

#smartsearch-serp .slider-base,#smartsearch-serp  .slider-connects {
  height: 100%;
  position: relative;
  width: 100%;
  z-index: 1
}

#smartsearch-serp .slider-connects {
  overflow: hidden;
  z-index: 0
}

#smartsearch-serp .slider-connect,#smartsearch-serp  .slider-origin {
  height: 100%;
  position: absolute;
  right: 0;
  top: 0;
  -ms-transform-origin: 0 0;
  -webkit-transform-origin: 0 0;
  transform-origin: 0 0;
  -webkit-transform-style: preserve-3d;
  transform-style: flat;
  width: 100%;
  will-change: transform;
  z-index: 1
}

#smartsearch-serp .slider-txt-dir-rtl.slider-horizontal #smartsearch-serp .slider-origin {
  left: 0;
  right: auto
}

#smartsearch-serp .slider-vertical .slider-origin {
  top: -100%;
  width: 0
}

#smartsearch-serp .slider-horizontal .slider-origin {
  height: 0
}

#smartsearch-serp .slider-handle {
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  position: absolute
}

#smartsearch-serp .slider-touch-area {
  height: 100%;
  width: 100%
}

#smartsearch-serp .slider-state-tap .slider-connect, #smartsearch-serp .slider-state-tap .slider-origin {
  transition: transform .3s
}

#smartsearch-serp .slider-state-drag * {
  cursor: inherit !important
}

#smartsearch-serp .slider-tooltip-drag .slider-tooltip, #smartsearch-serp .slider-tooltip-focus .slider-tooltip {
  display: none !important
}

#smartsearch-serp .slider-tooltip-drag .slider-active .slider-tooltip,
#smartsearch-serp .slider-tooltip-drag.slider-state-drag .slider-tooltip:not(.slider-tooltip-hidden),
#smartsearch-serp .slider-tooltip-focus.slider-focused .slider-tooltip:not(.slider-tooltip-hidden) {
  display: block !important
}

#smartsearch-serp .slider-horizontal {
  height: var(--slider-height, 6px)
}

#smartsearch-serp .slider-horizontal .slider-handle {
  height: var(--slider-handle-height, 16px);
  right: calc(var(--slider-handle-width, 16px) / 2 * -1);
  top: calc((var(--slider-handle-height, 16px) - var(--slider-height, 6px)) / 2 * -1 + -1px);
  width: var(--slider-handle-width, 16px)
}

#smartsearch-serp .slider-vertical {
  height: var(--slider-vertical-height, 300px);
  width: var(--slider-height, 6px)
}

#smartsearch-serp .slider-vertical .slider-handle {
  bottom: calc(var(--slider-handle-width, 16px) / 2 * -1);
  height: var(--slider-handle-width, 16px);
  right: calc((var(--slider-handle-height, 16px) - var(--slider-height, 6px)) / 2 * -1 + -1px);
  width: var(--slider-handle-height, 16px)
}

#smartsearch-serp .slider-txt-dir-rtl.slider-horizontal .slider-handle {
  left: calc(var(--slider-handle-width, 16px) / 2 * -1);
  right: auto
}

#smartsearch-serp .slider-base {
  background-color: var(--slider-bg, #d1d5db)
}

#smartsearch-serp .slider-base, #smartsearch-serp .slider-connects {
  border-radius: var(--slider-radius, 9999px)
}

#smartsearch-serp .slider-connect {
  background: var(--slider-connect-bg, #10b981);
  cursor: pointer
}

#smartsearch-serp .slider-draggable {
  cursor: ew-resize
}

#smartsearch-serp .slider-vertical .slider-draggable {
  cursor: ns-resize
}

#smartsearch-serp .slider-handle {
  background: var(--slider-handle-bg, #fff);
  border: var(--slider-handle-border, 0);
  border-radius: var(--slider-handle-radius, 9999px);
  box-shadow: var(--slider-handle-shadow, .5px .5px 2px 1px rgba(0, 0, 0, .32));
  cursor: -webkit-grab;
  cursor: grab;
  height: var(--slider-handle-height, 16px);
  width: var(--slider-handle-width, 16px)
}

#smartsearch-serp .slider-handle:focus {
  box-shadow: 0 0 0 var(--slider-handle-ring-width, 3px) var(--slider-handle-ring-color, rgba(16, 185, 129, .188)), var(--slider-handle-shadow, .5px .5px 2px 1px rgba(0, 0, 0, .32));
  outline: none
}

#smartsearch-serp .slider-active {
  box-shadow: var(--slider-handle-shadow-active, .5px .5px 2px 1px rgba(0, 0, 0, .42));
  cursor: -webkit-grabbing;
  cursor: grabbing
}

#smartsearch-serp [disabled] .slider-connect {
  background: var(--slider-connect-bg-disabled, #9ca3af)
}

#smartsearch-serp [disabled] .slider-handle,
#smartsearch-serp [disabled].slider-handle,
#smartsearch-serp [disabled].slider-target {
  cursor: not-allowed
}

#smartsearch-serp [disabled] .slider-tooltip {
  background: var(--slider-tooltip-bg-disabled, #9ca3af);
  border-color: var(--slider-tooltip-bg-disabled, #9ca3af)
}

#smartsearch-serp .slider-tooltip {
  background: var(--slider-tooltip-bg, #10b981);
  border: 1px solid var(--slider-tooltip-bg, #10b981);
  border-radius: var(--slider-tooltip-radius, 5px);
  color: var(--slider-tooltip-color, #fff);
  display: block;
  font-size: var(--slider-tooltip-font-size, .875em);
  font-weight: var(--slider-tooltip-font-weight, 600);
  line-height: var(--slider-tooltip-line-height, 1.25em);
  min-width: var(--slider-tooltip-min-width, 20px);
  padding: var(--slider-tooltip-py, 2px) var(--slider-tooltip-px, 6px);
  position: absolute;
  text-align: center;
  white-space: nowrap
}

#smartsearch-serp .slider-horizontal .slider-tooltip-top {
  bottom: calc(var(--slider-handle-height, 16px) + var(--slider-tooltip-arrow-size, 5px) + var(--slider-tooltip-distance, 3px));
  left: 50%;
  transform: translate(-50%)
}

#smartsearch-serp .slider-horizontal .slider-tooltip-top:before {
  border: var(--slider-tooltip-arrow-size, 5px) solid transparent;
  border-top-color: inherit;
  bottom: calc(var(--slider-tooltip-arrow-size, 5px) * -2);
  content: "";
  height: 0;
  left: 50%;
  position: absolute;
  transform: translate(-50%);
  width: 0
}

#smartsearch-serp .slider-horizontal .slider-tooltip-bottom {
  left: 50%;
  top: calc(var(--slider-handle-height, 16px) + var(--slider-tooltip-arrow-size, 5px) + var(--slider-tooltip-distance, 3px));
  transform: translate(-50%)
}

#smartsearch-serp .slider-horizontal .slider-tooltip-bottom:before {
  border: var(--slider-tooltip-arrow-size, 5px) solid transparent;
  border-bottom-color: inherit;
  content: "";
  height: 0;
  left: 50%;
  position: absolute;
  top: calc(var(--slider-tooltip-arrow-size, 5px) * -2);
  transform: translate(-50%);
  width: 0
}

#smartsearch-serp .slider-vertical .slider-tooltip-left {
  right: calc(var(--slider-handle-height, 16px) + var(--slider-tooltip-arrow-size, 5px) + var(--slider-tooltip-distance, 3px));
  top: 50%;
  transform: translateY(-50%)
}

#smartsearch-serp .slider-vertical .slider-tooltip-left:before {
  border: var(--slider-tooltip-arrow-size, 5px) solid transparent;
  border-left-color: inherit;
  content: "";
  height: 0;
  position: absolute;
  right: calc(var(--slider-tooltip-arrow-size, 5px) * -2);
  top: 50%;
  transform: translateY(-50%);
  width: 0
}

#smartsearch-serp .slider-vertical .slider-tooltip-right {
  left: calc(var(--slider-handle-height, 16px) + var(--slider-tooltip-arrow-size, 5px) + var(--slider-tooltip-distance, 3px));
  top: 50%;
  transform: translateY(-50%)
}

#smartsearch-serp .slider-vertical .slider-tooltip-right:before {
  border: var(--slider-tooltip-arrow-size, 5px) solid transparent;
  border-right-color: inherit;
  content: "";
  height: 0;
  left: calc(var(--slider-tooltip-arrow-size, 5px) * -2);
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 0
}

#smartsearch-serp .slider-horizontal .slider-origin > .slider-tooltip {
  left: auto;
  transform: translate(50%)
}

#smartsearch-serp .slider-horizontal .slider-origin > .slider-tooltip-top {
  bottom: calc(var(--slider-tooltip-arrow-size, 5px) + (var(--slider-handle-height, 16px) - var(--slider-height, 6px)) / 2 + var(--slider-tooltip-distance, 3px) + 1px)
}

#smartsearch-serp .slider-horizontal .slider-origin > .slider-tooltip-bottom {
  top: calc(var(--slider-tooltip-arrow-size, 5px) + (var(--slider-handle-height, 16px) - var(--slider-height, 6px)) / 2 + var(--slider-tooltip-distance, 3px) + var(--slider-height, 6px) - 1px)
}

#smartsearch-serp .slider-vertical .slider-origin > .slider-tooltip {
  top: auto;
  transform: translateY(calc((var(--slider-tooltip-line-height, 1.25em) - var(--slider-tooltip-py, 2px)) * -1 + 1px))
}

#smartsearch-serp .slider-vertical .slider-origin > .slider-tooltip-left {
  right: calc(var(--slider-tooltip-arrow-size, 5px) + var(--slider-height, 6px) + (var(--slider-handle-height, 16px) - var(--slider-height, 6px)) / 2 + var(--slider-tooltip-distance, 3px) - 1px)
}

#smartsearch-serp .slider-vertical .slider-origin > .slider-tooltip-right {
  left: calc(var(--slider-tooltip-arrow-size, 5px) + var(--slider-height, 6px) + (var(--slider-handle-height, 16px) - var(--slider-height, 6px)) / 2 + var(--slider-tooltip-distance, 3px) - var(--slider-height, 6px) + 1px)
}

:root {
  --slider-connect-bg: #dc3545;
  --slider-tooltip-bg: #dc3545;
  --slider-handle-ring-color: #dc3545;
}

#smartsearch-serp .slider-handle.slider-handle-upper .slider-tooltip-top,
#smartsearch-serp .slider-handle.slider-handle-lower .slider-tooltip-top {
  display: none;
}

#smartsearch-serp .slider-handle.slider-handle-upper.slider-active .slider-tooltip-top,
#smartsearch-serp .slider-handle.slider-handle-lower.slider-active .slider-tooltip-top {
  display: unset !important;
}

#smartsearch-serp .dropdown .serp__filter-item--rangeSlider {
  overflow: visible !important;
}

#smartsearch-serp .dropdown .multi-range__max-container {
  margin-left: 0.5em;
}

</style>